import { getRequestOptions, postRequestOptions, getAxiosConfig, getAxiosConfigWithContentType } from '../helpers/Fetchwrapper';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

export const OrderService = {
    /**
     * Get orders list with filtering and pagination
     */
    getOrdersList: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams({
                offset: params.offset || 0,
                limit: params.limit || 10,
                orderBy: params.orderBy || 'order_id',
                orderType: params.orderType || 'desc',
                globalSearch: params.globalSearch || '',
                ...params
            }).toString();

            const response = await fetch(
                `${API_URL}orders/list?${queryParams}`,
                getRequestOptions()
            );
            return await response.json();
        } catch (error) {
            console.error('Error fetching orders:', error);
            throw error;
        }
    },

    /**
     * Get order details by ID
     */
    getOrderDetails: async (orderId) => {
        try {
            const response = await fetch(
                `${API_URL}orders/details/${orderId}`,
                getRequestOptions()
            );
            return await response.json();
        } catch (error) {
            console.error('Error fetching order details:', error);
            throw error;
        }
    },

    /**
     * Create new order
     */
    createOrder: async (orderData) => {
        try {
            const response = await fetch(
                `${API_URL}orders/create`,
                postRequestOptions(orderData)
            );
            return await response.json();
        } catch (error) {
            console.error('Error creating order:', error);
            throw error;
        }
    },

    /**
     * Change order status
     */
    changeOrderStatus: async (orderData) => {
        try {
            const response = await fetch(
                `${API_URL}orders/change-status`,
                postRequestOptions(orderData)
            );
            return await response.json();
        } catch (error) {
            console.error('Error changing order status:', error);
            throw error;
        }
    },

    /**
     * Get products list for order creation
     */
    getProductsList: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams({
                offset: params.offset || 0,
                limit: params.limit || 10,
                globalSearch: params.globalSearch || '',
                ...params
            }).toString();

            const response = await fetch(
                `${API_URL}orders/products?${queryParams}`,
                getRequestOptions()
            );
            return await response.json();
        } catch (error) {
            console.error('Error fetching products:', error);
            throw error;
        }
    },

    /**
     * Get customers list for order creation
     */
    getCustomersList: async (params = {}) => {
        try {
            const queryParams = new URLSearchParams({
                offset: params.offset || 0,
                limit: params.limit || 10,
                globalSearch: params.globalSearch || '',
                ...params
            }).toString();

            const response = await fetch(
                `${API_URL}orders/customers?${queryParams}`,
                getRequestOptions()
            );
            return await response.json();
        } catch (error) {
            console.error('Error fetching customers:', error);
            throw error;
        }
    },

    /**
     * Get shipping methods
     */
    getShippingMethods: async () => {
        try {
            const response = await fetch(
                `${API_URL}orders/shipping-methods`,
                getRequestOptions()
            );
            return await response.json();
        } catch (error) {
            console.error('Error fetching shipping methods:', error);
            throw error;
        }
    },

    /**
     * Print order
     */
    printOrder: async (orderId) => {
        try {
            const response = await fetch(
                `${API_URL}orders/print/${orderId}`,
                getRequestOptions()
            );
            
            if (response.ok) {
                const blob = await response.blob();
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `order-${orderId}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                return { success: true };
            } else {
                throw new Error('Failed to generate PDF');
            }
        } catch (error) {
            console.error('Error printing order:', error);
            throw error;
        }
    },

    /**
     * Get order status options
     */
    getOrderStatusOptions: () => {
        return [
            { value: 'pending', label: 'Pending', color: 'warning' },
            { value: 'confirm', label: 'Confirmed', color: 'info' },
            { value: 'processing', label: 'Processing', color: 'primary' },
            { value: 'shipped', label: 'Shipped', color: 'success' },
            { value: 'delivered', label: 'Delivered', color: 'success' },
            { value: 'canceled', label: 'Canceled', color: 'danger' },
            { value: 'refunded', label: 'Refunded', color: 'secondary' }
        ];
    },

    /**
     * Format order status for display
     */
    formatOrderStatus: (status) => {
        const statusOptions = OrderService.getOrderStatusOptions();
        const statusOption = statusOptions.find(option => option.value === status);
        return statusOption || { value: status, label: status, color: 'secondary' };
    },

    /**
     * Format currency
     */
    formatCurrency: (amount) => {
        return new Intl.NumberFormat('en-BD', {
            style: 'currency',
            currency: 'BDT',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        }).format(amount || 0);
    },

    /**
     * Format date
     */
    formatDate: (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    }
};

export default OrderService; 